import { Container, Graphics } from "pixi.js";

import type {
  IOverlayElement,
  IStudioOverlay,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";
import type { OverlayVariables } from "../../../../components/AreaOverlay/OverlayVariablesContext";
import { decodeVariables } from "../../../../screens/OverlayBuilder/components/OverlayPropertyPanel.utils";
import { resolveOverlayDesignSize } from "../../../../entities/composition/designSize";

import OverlayImageRenderer from "./OverlayImageRenderer";

import OverlayVideoRenderer from "./OverlayVideoRenderer";

import OverlayShapeRenderer from "./OverlayShapeRenderer";

import OverlayTextRenderer from "./OverlayTextRenderer";

import OverlayParticlesRenderer from "./OverlayParticlesRenderer";

import OverlayProceduralRenderer from "./OverlayProceduralRenderer";

import {
  applyOverlayAnimation,
  type OverlayFrameUpdater,
} from "./OverlayElementAnimator";

import { applyShaderPreset } from "./OverlayShaderPreset";

interface IDesignSize {
  width: number;

  height: number;
}

/**
 * Containers de efectos de un elemento con animación y/o shaderPreset. `fx` es
 * el que anima `applyOverlayAnimation` (pivot = `origin` para keyframes, centro
 * del elemento para el resto) y el que recibe los filtros. Si el elemento tiene
 * keyframes Y rotación estática, `fx` cuelga de un `rot` PADRE con esa rotación
 * alrededor del centro — el mismo orden que el DOM (`ElementWrapper` rotado →
 * `AnimatedLayer`): una x animada se desplaza sobre el eje ya rotado. En ese
 * caso `drawn` es el elemento con rotation 0, para que el render per-kind no
 * vuelva a rotarlo. Si no, `root` es el propio `fx` y `drawn` el elemento.
 */
export const createOverlayFxContainers = (
  element: IOverlayElement,
): { root: Container; fx: Container; drawn: IOverlayElement } => {
  const { left, top, width: dw, height: dh } = element.bounds;
  const isKeyframes = element.animation?.kind === "keyframes";
  const origin =
    element.animation?.kind === "keyframes"
      ? element.animation.origin
      : undefined;
  const fx = new Container();
  const px = left + (origin ? origin.x : dw / 2);
  const py = top + (origin ? origin.y : dh / 2);
  fx.pivot.set(px, py);
  fx.position.set(px, py);
  if (!isKeyframes || !element.rotation) {
    return { root: fx, fx, drawn: element };
  }
  const rot = new Container();
  rot.pivot.set(left + dw / 2, top + dh / 2);
  rot.position.set(left + dw / 2, top + dh / 2);
  rot.rotation = (element.rotation * Math.PI) / 180;
  rot.addChild(fx);
  return { root: rot, fx, drawn: { ...element, rotation: 0 } };
};

export class CustomOverlayRenderer {
  private shapeRenderer = new OverlayShapeRenderer();

  private textRenderer = new OverlayTextRenderer();

  private imageRenderer = new OverlayImageRenderer();

  private videoRenderer = new OverlayVideoRenderer();

  private particlesRenderer = new OverlayParticlesRenderer();

  private proceduralRenderer = new OverlayProceduralRenderer();

  /**
   * Renderiza el overlay y devuelve los updaters per-frame (animación de
   * elementos, shaders animados, partículas, procedural, typewriter) para que
   * el caller los ejecute cada frame vía ticker. Sin nada animado devuelve `[]`.
   */
  render(
    overlay: IStudioOverlay,
    container: Container,
    areaSize: IDesignSize,
    variables?: OverlayVariables,
    sink?: OverlayFrameUpdater[],
  ): OverlayFrameUpdater[] {
    // Resuelve los DEFAULTS de las variables del overlay (name → defaultValue)
    // para que `{var}` en el texto interpole su valor por defecto en vez de
    // salir como token literal al stream. `variables` (override del runtime
    // live, ej. follower count) tiene precedencia cuando se provea.
    const vars: OverlayVariables = {};
    for (const entry of decodeVariables(overlay.options.variables)) {
      if (entry.name) vars[entry.name] = entry.defaultValue;
    }
    if (variables) Object.assign(vars, variables);

    // designSize explícito > tabla por ratio > rect del área: el MISMO criterio
    // que los mounts DOM (AreaOverlayMount / SceneMaskMount), vía
    // resolveOverlayDesignSize, así los px de diseño escalan igual en ambos.
    const design = resolveOverlayDesignSize(overlay.options, areaSize);

    const designWidth = design.width || areaSize.width || 1;

    const designHeight = design.height || areaSize.height || 1;

    const inner = new Container();

    inner.scale.set(
      areaSize.width / designWidth,
      areaSize.height / designHeight,
    );

    container.addChild(inner);

    const elements = overlay.options.elements ?? [];

    const sorted = elements
      .filter((el: IOverlayElement) => el.hidden !== true)
      .slice()
      .sort((a, b) => (a.bounds.zIndex ?? 0) - (b.bounds.zIndex ?? 0));

    // `sink`: lista viva del llamador; las registraciones tardías (typewriter
    // tras cargar la fuente) caen en la misma instancia.
    const updaters: OverlayFrameUpdater[] = sink ?? [];

    for (const element of sorted) {
      // Element bounds are stored as absolute pixels in design space (e.g.
      // `{ left: 60, top: 60, width: 1800, height: 70 }` against a 1920×
      // 1080 design canvas). The DOM renderer styles them with
      // `left: ${bounds.left}px` directly. The `inner` container's
      // scale.set(...) above maps design-pixels to live area-pixels, so
      // we forward bounds verbatim.
      const dw = element.bounds.width;

      const dh = element.bounds.height;

      const destination = { width: dw, height: dh };

      // Container de efectos `fx`: se interpone cuando el elemento tiene
      // animación y/o shaderPreset (ver createOverlayFxContainers). Pivot =
      // centro del elemento (u `origin` en keyframes), así scale-in/pulse
      // escalan desde el centro y los slide-in usan offset de posición; el
      // render per-kind sigue posicionando su wrapper en (left, top) y el
      // pivot+position del fx se compensan para que en base quede en su lugar.
      // Los filtros (shader) se aplican a este mismo container. `drawn` es lo
      // que ve el render per-kind (rotation 0 si ya la puso el `rot` padre).
      let target: Container = inner;
      let drawn: IOverlayElement = element;
      if (element.animation || element.shaderPreset) {
        const tree = createOverlayFxContainers(element);
        inner.addChild(tree.root);
        target = tree.fx;
        drawn = tree.drawn;

        if (element.shaderPreset) {
          const u = applyShaderPreset(tree.fx, element.shaderPreset);
          if (u) updaters.push(u);
        }
        if (element.animation) {
          const handle = {
            container: tree.fx,
            animation: element.animation,
            w: dw,
            h: dh,
          };
          updaters.push((t) => applyOverlayAnimation(handle, t));
        }
      }

      if (drawn.kind === "shape") {
        this.shapeRenderer.render(target, { element: drawn, destination });
      } else if (drawn.kind === "text") {
        this.textRenderer.render(target, {
          element: drawn,
          destination,
          variables: vars,
          registerUpdater: (u) => updaters.push(u),
        });
      } else if (drawn.kind === "image") {
        this.imageRenderer.render(target, { element: drawn, destination });
      } else if (drawn.kind === "video") {
        this.videoRenderer.render(target, { element: drawn, destination });
      } else if (drawn.kind === "particles") {
        const u = this.particlesRenderer.render(target, {
          element: drawn,
          destination,
        });
        if (u) updaters.push(u);
      } else if (drawn.kind === "procedural") {
        const u = this.proceduralRenderer.render(target, {
          element: drawn,
          destination,
        });
        if (u) updaters.push(u);
      }
    }

    // Clip elements drawn outside the area rect — matches the DOM overflow:hidden
    const mask = new Graphics();

    mask.rect(0, 0, areaSize.width, areaSize.height).fill(0xffffff);

    container.addChild(mask);

    container.mask = mask;

    return updaters;
  }
}

export default CustomOverlayRenderer;
